import React, { useState, useContext, useCallback, useEffect } from "react";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Avatar,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Stack,
  CircularProgress,
  Backdrop,
  useTheme,
  alpha,
  InputAdornment,
  Autocomplete,
  Switch,
  FormControlLabel,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  QrCode as QrCodeIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  Diamond as DiamondIcon,
  Scale as ScaleIcon,
  AttachMoney as AttachMoneyIcon,
  Tag as TagIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  ShortText as ShortTextIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { DataContext } from "../../context/DataContext";
import { styled } from "@mui/material/styles";
import QRCode from "qrcode";
import ImageUploadField from '../../components/CloudinaryImageUploadField';

// Add this loading component before the AddItemPage component

const LoadingShimmer = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: "-100%",
          width: "100%",
          height: "100%",
          background: `linear-gradient(
            90deg,
            transparent,
            ${alpha(isDark ? "#ffffff" : "#000000", 0.05)},
            transparent
          )`,
          animation: "shimmer 1.5s infinite",
          zIndex: 1,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDark
            ? "radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.08) 0%, transparent 50%)"
            : "radial-gradient(circle at 20% 80%, rgba(16, 185, 129, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.06) 0%, transparent 50%)",
          zIndex: 0,
        },
        "@keyframes shimmer": {
          "0%": {
            left: "-100%",
          },
          "100%": {
            left: "100%",
          },
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, py: 4 }}>
        {/* Header Shimmer */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              height: 100,
              borderRadius: 2,
              background: alpha(isDark ? "#1e293b" : "#f1f5f9", 0.6),
              backdropFilter: "blur(20px)",
              border: `1px solid ${alpha(isDark ? "#334155" : "#e2e8f0", 0.3)}`,
              position: "relative",
              overflow: "hidden",
            }}
          />
        </Box>

        {/* Management Buttons Shimmer */}
        <Box
          sx={{ display: "flex", gap: 2, mb: 2, justifyContent: "flex-end" }}
        >
          {[1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 150,
                height: 36,
                borderRadius: 2,
                background: alpha(isDark ? "#1e293b" : "#f1f5f9", 0.6),
                backdropFilter: "blur(10px)",
                border: `1px solid ${alpha(
                  isDark ? "#334155" : "#e2e8f0",
                  0.3
                )}`,
                position: "relative",
                overflow: "hidden",
              }}
            />
          ))}
        </Box>

        {/* Toggle Shimmer */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            mb: 2,
            background: alpha(isDark ? "#1e293b" : "#f1f5f9", 0.6),
            backdropFilter: "blur(10px)",
            border: `1px solid ${alpha(isDark ? "#334155" : "#e2e8f0", 0.3)}`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: 300,
              height: 42,
              borderRadius: 1,
              background: alpha(isDark ? "#2d3748" : "#e2e8f0", 0.5),
              mb: 1,
            }}
          />
          <Box
            sx={{
              width: 400,
              height: 20,
              borderRadius: 1,
              background: alpha(isDark ? "#2d3748" : "#e2e8f0", 0.5),
            }}
          />
        </Box>

        <Grid container spacing={3}>
          {/* Form Section Shimmer */}
          <Grid item xs={12} md={8} sx={{ margin: "auto", width: "70%" }}>
            {[1, 2, 3, 4].map((i) => (
              <Box
                key={i}
                sx={{
                  borderRadius: 3,
                  mb: 3,
                  background: alpha(isDark ? "#1e293b" : "#f1f5f9", 0.6),
                  backdropFilter: "blur(20px)",
                  border: `1px solid ${alpha(
                    isDark ? "#334155" : "#e2e8f0",
                    0.3
                  )}`,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Box sx={{ p: 3, m: "auto", width: "100%" }}>
                  <Box
                    sx={{
                      width: 200,
                      height: 32,
                      borderRadius: 1,
                      background: alpha(isDark ? "#2d3748" : "#e2e8f0", 0.5),
                      mb: 3,
                    }}
                  />
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4].map((j) => (
                      <Grid item xs={12} md={6} key={j}>
                        <Box
                          sx={{
                            height: 56,
                            borderRadius: 2,
                            background: alpha(
                              isDark ? "#2d3748" : "#e2e8f0",
                              0.5
                            ),
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            ))}

            {/* Action Buttons Shimmer */}
            <Box
              sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 2 }}
            >
              <Box
                sx={{
                  width: 180,
                  height: 48,
                  borderRadius: 3,
                  background: alpha(isDark ? "#1e293b" : "#f1f5f9", 0.6),
                }}
              />
              <Box
                sx={{
                  width: 120,
                  height: 48,
                  borderRadius: 3,
                  background: alpha(isDark ? "#1e293b" : "#f1f5f9", 0.6),
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// StyledTextField for regular text fields
const StyledTextField = styled(TextField)(({ theme, fullWidth }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 20,
    backdropFilter: "blur(10px)",
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.background.paper, 0.3)
        : alpha("#ffffff", 0.8),
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.background.paper, 0.5)
          : alpha("#ffffff", 0.9),
    },
    "&.Mui-focused": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.background.paper, 0.5)
          : alpha("#ffffff", 0.9),
    },
  },
  "& .MuiInputLabel-root": {
    color: theme.palette.mode === "dark" ? "#cbd5e1" : "#64748b",
  },
  mb: 2,
  width: fullWidth ? "100%" : "300px",
}));

// Gross Weight Product Toggle
const GrossWeightToggle = styled(Paper)(({ theme, isDark }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backdropFilter: "blur(10px)",
  backgroundColor: alpha(
    isDark ? theme.palette.background.paper : "#ffffff",
    isDark ? 0.3 : 0.8
  ),
  border: `1px solid ${alpha(
    isDark ? theme.palette.divider : "#e2e8f0",
    isDark ? 0.2 : 0.3
  )}`,
  marginBottom: theme.spacing(2),
  "&:hover": {
    borderColor: alpha("#8B5CF6", isDark ? 0.4 : 0.3),
  },
}));

const AddItemPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [initialLoading, setInitialLoading] = useState(true);

  const {
    stock,
    loading,
    addStock,
    bulkUpdateSilverStock,
    suppliers,
    companyDetails,
    updateCompanyDetails,
  } = useContext(DataContext);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Get current company ID (assuming first company or from session)
  const currentCompanyId = companyDetails?.[0]?.id;

  // State for dynamic item categories, metals, and silver types
  const [itemCategories, setItemCategories] = useState([]);
  const [metalTypes, setMetalTypes] = useState([]);
  const [silverTypes, setSilverTypes] = useState([]);

  // Default silver types if none in company details
  const defaultSilverTypes = useCallback(
    () => [
      {
        id: "1",
        name: "999silver",
        displayName: "999 Silver",
        shortcut: "999",
      },
      {
        id: "2",
        name: "925silver",
        displayName: "925 Silver",
        shortcut: "925",
      },
      {
        id: "3",
        name: "mixsilver",
        displayName: "Mix Silver",
        shortcut: "MIX",
      },
      { id: "4", name: "silver", displayName: "Silver", shortcut: "SIL" },
      {
        id: "5",
        name: "oldsilver",
        displayName: "Old Silver",
        shortcut: "OLD",
      },
    ],
    []
  );

  // Dialog states
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [metalDialogOpen, setMetalDialogOpen] = useState(false);
  const [silverTypeDialogOpen, setSilverTypeDialogOpen] = useState(false);

  // Form states for add/edit
  const [newItemName, setNewItemName] = useState("");
  const [newItemShortcut, setNewItemShortcut] = useState("");
  const [newMetalName, setNewMetalName] = useState("");
  const [newMetalShortcut, setNewMetalShortcut] = useState("");
  const [newSilverType, setNewSilverType] = useState("");
  const [newSilverTypeDisplayName, setNewSilverTypeDisplayName] = useState("");
  const [newSilverTypeShortcut, setNewSilverTypeShortcut] = useState("");

  // Edit states
  const [editingItem, setEditingItem] = useState(null);
  const [editingMetal, setEditingMetal] = useState(null);
  const [editingSilverType, setEditingSilverType] = useState(null);

  // Loading states
  const [isUpdatingMasterData, setIsUpdatingMasterData] = useState(false);

  const [generatedQR, setGeneratedQR] = useState(null);
  const [qrImage, setQrImage] = useState("");
  const [lastAddedItem, setLastAddedItem] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dynamicFields, setDynamicFields] = useState({});
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [isGrossWeightProduct, setIsGrossWeightProduct] = useState(false);

  // Load master data from company details
  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];
      console.log(company, "Company details in AddItemPage");

      // Load item categories
      if (company.items && Array.isArray(company.items)) {
        setItemCategories(company.items);
      } else {
        setItemCategories([]);
      }

      // Load metal types
      if (company.metals && Array.isArray(company.metals)) {
        setMetalTypes(company.metals);
      } else {
        setMetalTypes([]);
      }

      // Load silver types (custom from company)
      if (company.silverTypes && Array.isArray(company.silverTypes)) {
        setSilverTypes(company.silverTypes);
      } else {
        // If no silver types in company, use defaults but don't save them yet
        setSilverTypes(defaultSilverTypes);
      }
    }
  }, [companyDetails, defaultSilverTypes]);

  // Fetch suppliers from context or API
  const fetchSuppliers = useCallback(async () => {
    setLoadingSuppliers(true);
    try {
      if (suppliers && suppliers.length > 0) {
        const formattedSuppliers = suppliers.map((supplier) => ({
          id: supplier.id || supplier._id,
          name: supplier.name || supplier.supplierName || "",
          shortcut: supplier.shortcut || supplier.supplierShortcut || "",
        }));
        setSupplierOptions(formattedSuppliers);
      } else {
        const uniqueSuppliers = [
          ...new Set(
            stock
              .filter((item) => item.supplierName)
              .map((item) => ({
                name: item.supplierName,
                shortcut: item.supplierShortcut || "",
              }))
          ),
        ];

        const seen = new Set();
        const dedupedSuppliers = uniqueSuppliers.filter((supplier) => {
          const duplicate = seen.has(supplier.name);
          seen.add(supplier.name);
          return !duplicate;
        });

        setSupplierOptions(dedupedSuppliers);
      }
    } catch (error) {
      console.error("Error loading suppliers:", error);
    } finally {
      setLoadingSuppliers(false);
    }
  }, [suppliers, stock]);

  useEffect(() => {
    if (stock && stock.length > 0) {
      fetchSuppliers();
    }
  }, [stock, fetchSuppliers]);

  // Format text to capital case
  const toCapitalCase = useCallback((str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, []);

  // Format to uppercase
  const toUpperCase = useCallback((str) => {
    return str ? str.toUpperCase() : "";
  }, []);
  const [resetKey, setResetKey] = useState(0);

  // State to persist form values
  const [persistedFormValues, setPersistedFormValues] = useState({
    supplierName: "",
    supplierShortcut: "",
    name: "",
    metal: "Gold",
    silverType: "",
    melting: "92",
    grossWeight: "",
    lessWeight: "",
    weight: "",
    silverRate: "",
    labour: "",
    goldCuttingRate: "",
    wastage: "",
    makingCharges: "",
    serialNo: "",
    description: "",
    huidCodes: [""],
    itemDetail: "",
    miscitemName: "",
    miscitemShortcut: "",
    imageUrl: "",
  });

  // Initial form values - use persisted values
  const initialValues = persistedFormValues;

  // Helper function to get item shortcut
  // const getItemShortcut = useCallback((itemName) => {
  //   if (!itemName) return '';
  //   const item = itemCategories.find(cat =>
  //     cat.name.toLowerCase() === itemName.toLowerCase()
  //   );
  //   return item ? item.shortcut : itemName.substring(0, 3).toUpperCase();
  // }, [itemCategories]);

  // // Helper function to get metal shortcut
  // const getMetalShortcut = useCallback((metalName) => {
  //   if (!metalName) return '';
  //   const metal = metalTypes.find(m =>
  //     m.name.toLowerCase() === metalName.toLowerCase()
  //   );
  //   return metal ? metal.shortcut : metalName.substring(0, 2).toUpperCase();
  // }, [metalTypes]);

  // Helper function to update company master data
  const updateCompanyMasterData = useCallback(
    async (updatedMasterData) => {
      if (!currentCompanyId) {
        console.error("No company ID found");
        alert("No company selected. Please contact administrator.");
        return { success: false, message: "No company selected" };
      }

      setIsUpdatingMasterData(true);
      try {
        // Get current company details
        const currentCompany = companyDetails[0];

        // Merge with updated master data
        const updatedCompany = {
          ...currentCompany,
          ...updatedMasterData,
        };

        // Call update API
        const result = await updateCompanyDetails(
          currentCompanyId,
          updatedCompany
        );

        if (result.success) {
          return { success: true, message: "Master data updated successfully" };
        } else {
          throw new Error(result.message || "Failed to update master data");
        }
      } catch (error) {
        console.error("Error updating master data:", error);
        return { success: false, message: error.message };
      } finally {
        setIsUpdatingMasterData(false);
      }
    },
    [currentCompanyId, companyDetails, updateCompanyDetails]
  );

  // ========= ITEM CATEGORY FUNCTIONS =========
  const handleAddItemCategory = useCallback(async () => {
    if (!newItemName.trim() || !newItemShortcut.trim()) {
      alert("Please enter both name and shortcut");
      return;
    }

    const duplicateItem = itemCategories.find(
      (item) => item.name.toLowerCase() === newItemName.trim().toLowerCase()
    );

    if (duplicateItem) {
      alert(`❌ Item "${newItemName.trim()}" already exists!\n\nPlease use a different item name.`);
      return;
    }

    // Check for duplicate shortcut (case-insensitive)
    const duplicateShortcut = itemCategories.find(
      (item) => item.shortcut.toLowerCase() === newItemShortcut.trim().toLowerCase()
    );

    if (duplicateShortcut) {
      alert(`❌ Shortcut "${newItemShortcut.trim()}" already exists!\n\nPlease use a different shortcut.`);
      return;
    }

    const newItem = {
      id: Date.now().toString(),
      name: toCapitalCase(newItemName),
      shortcut: toUpperCase(newItemShortcut).slice(0, 3),
    };

    const updatedItems = [...itemCategories, newItem];
    const result = await updateCompanyMasterData({ items: updatedItems });

    if (result.success) {
      setItemCategories(updatedItems);
      setNewItemName("");
      setNewItemShortcut("");
      setItemDialogOpen(false);
      alert("✅ Item category added successfully");
    } else {
      alert("❌ Failed to add item category: " + result.message);
    }
  }, [
    newItemName,
    newItemShortcut,
    itemCategories,
    updateCompanyMasterData,
    toCapitalCase,
    toUpperCase,
  ]);

  const handleUpdateItemCategory = useCallback(async () => {
    if (!newItemName.trim() || !newItemShortcut.trim() || !editingItem) {
      alert("Please enter both name and shortcut");
      return;
    }

    // Check for duplicate item name (excluding the current item being edited)
    const duplicateItem = itemCategories.find(
      (item) =>
        item.id !== editingItem.id &&
        item.name.toLowerCase() === newItemName.trim().toLowerCase()
    );

    if (duplicateItem) {
      alert(`❌ Item "${newItemName.trim()}" already exists!\n\nPlease use a different item name.`);
      return;
    }

    // Check for duplicate shortcut (excluding the current item being edited)
    const duplicateShortcut = itemCategories.find(
      (item) =>
        item.id !== editingItem.id &&
        item.shortcut.toLowerCase() === newItemShortcut.trim().toLowerCase()
    );

    if (duplicateShortcut) {
      alert(`❌ Shortcut "${newItemShortcut.trim()}" already exists!\n\nPlease use a different shortcut.`);
      return;
    }

    const updatedItems = itemCategories.map((item) =>
      item.id === editingItem.id
        ? {
          ...item,
          name: toCapitalCase(newItemName),
          shortcut: toUpperCase(newItemShortcut).slice(0, 3),
        }
        : item
    );

    const result = await updateCompanyMasterData({ items: updatedItems });

    if (result.success) {
      setItemCategories(updatedItems);
      setNewItemName("");
      setNewItemShortcut("");
      setEditingItem(null);
      setItemDialogOpen(false);
      alert("✅ Item category updated successfully");
    } else {
      alert("❌ Failed to update item category: " + result.message);
    }
  }, [
    newItemName,
    newItemShortcut,
    editingItem,
    itemCategories,
    updateCompanyMasterData,
    toCapitalCase,
    toUpperCase,
  ]);

  const handleDeleteItemCategory = useCallback(
    async (id) => {
      const itemToDelete = itemCategories.find((item) => item.id === id);
      if (!itemToDelete) return;

      // Check if this item category is used in any UNSOLD stock items
      const usedInUnsoldStock = stock.some(
        (stockItem) =>
          stockItem.name === itemToDelete.name &&
          (stockItem.sold === "No" || stockItem.sold === false)
      );

      if (usedInUnsoldStock) {
        alert(
          `❌ Cannot delete "${itemToDelete.name}"\n\nThis item category is currently being used by unsold stock items. Please sell or delete those stock items first.`
        );
        return;
      }

      // Check if it's used in any sold stock items (for information only)
      const usedInSoldStock = stock.some(
        (stockItem) =>
          stockItem.name === itemToDelete.name &&
          (stockItem.sold === "Yes" || stockItem.sold === true)
      );

      const confirmMessage = usedInSoldStock
        ? `This item category "${itemToDelete.name}" has been used in sold items. Are you sure you want to delete it?`
        : `Are you sure you want to delete "${itemToDelete.name}"?`;

      if (!window.confirm(confirmMessage)) {
        return;
      }

      const updatedItems = itemCategories.filter((item) => item.id !== id);
      const result = await updateCompanyMasterData({ items: updatedItems });

      if (result.success) {
        setItemCategories(updatedItems);
        alert("✅ Item category deleted successfully");
      } else {
        alert("❌ Failed to delete item category: " + result.message);
      }
    },
    [itemCategories, updateCompanyMasterData, stock]
  );


  // ========= METAL TYPE FUNCTIONS =========
  const handleAddMetalType = useCallback(async () => {
    if (!newMetalName.trim() || !newMetalShortcut.trim()) {
      alert("Please enter both name and shortcut");
      return;
    }

    // Check for duplicate metal name (case-insensitive)
    const duplicateMetal = metalTypes.find(
      (metal) => metal.name.toLowerCase() === newMetalName.trim().toLowerCase()
    );

    if (duplicateMetal) {
      alert(`❌ Metal "${newMetalName.trim()}" already exists!\n\nPlease use a different metal name.`);
      return;
    }

    // Check for duplicate shortcut (case-insensitive)
    const duplicateShortcut = metalTypes.find(
      (metal) => metal.shortcut.toLowerCase() === newMetalShortcut.trim().toLowerCase()
    );

    if (duplicateShortcut) {
      alert(`❌ Shortcut "${newMetalShortcut.trim()}" already exists!\n\nPlease use a different shortcut.`);
      return;
    }

    const newMetal = {
      id: Date.now().toString(),
      name: toCapitalCase(newMetalName),
      shortcut: toUpperCase(newMetalShortcut).slice(0, 2),
    };

    const updatedMetals = [...metalTypes, newMetal];
    const result = await updateCompanyMasterData({ metals: updatedMetals });

    if (result.success) {
      setMetalTypes(updatedMetals);
      setNewMetalName("");
      setNewMetalShortcut("");
      setMetalDialogOpen(false);
      alert("✅ Metal type added successfully");
    } else {
      alert("❌ Failed to add metal type: " + result.message);
    }
  }, [
    newMetalName,
    newMetalShortcut,
    metalTypes,
    updateCompanyMasterData,
    toCapitalCase,
    toUpperCase,
  ]);

  const handleUpdateMetalType = useCallback(async () => {
    if (!newMetalName.trim() || !newMetalShortcut.trim() || !editingMetal) {
      alert("Please enter both name and shortcut");
      return;
    }

    const duplicateMetal = metalTypes.find(
      (metal) =>
        metal.id !== editingMetal.id &&
        metal.name.toLowerCase() === newMetalName.trim().toLowerCase()
    );

    if (duplicateMetal) {
      alert(`❌ Metal "${newMetalName.trim()}" already exists!\n\nPlease use a different metal name.`);
      return;
    }

    // Check for duplicate shortcut (excluding the current metal being edited)
    const duplicateShortcut = metalTypes.find(
      (metal) =>
        metal.id !== editingMetal.id &&
        metal.shortcut.toLowerCase() === newMetalShortcut.trim().toLowerCase()
    );

    if (duplicateShortcut) {
      alert(`❌ Shortcut "${newMetalShortcut.trim()}" already exists!\n\nPlease use a different shortcut.`);
      return;
    }

    const updatedMetals = metalTypes.map((metal) =>
      metal.id === editingMetal.id
        ? {
          ...metal,
          name: toCapitalCase(newMetalName),
          shortcut: toUpperCase(newMetalShortcut).slice(0, 2),
        }
        : metal
    );

    const result = await updateCompanyMasterData({ metals: updatedMetals });

    if (result.success) {
      setMetalTypes(updatedMetals);
      setNewMetalName("");
      setNewMetalShortcut("");
      setEditingMetal(null);
      setMetalDialogOpen(false);
      alert("✅ Metal type updated successfully");
    } else {
      alert("❌ Failed to update metal type: " + result.message);
    }
  }, [
    newMetalName,
    newMetalShortcut,
    editingMetal,
    metalTypes,
    updateCompanyMasterData,
    toCapitalCase,
    toUpperCase,
  ]);

  const handleDeleteMetalType = useCallback(
    async (id) => {
      const metalToDelete = metalTypes.find((metal) => metal.id === id);
      if (!metalToDelete) return;

      // Check if this metal type is used in any UNSOLD stock items
      const usedInUnsoldStock = stock.some(
        (stockItem) =>
          stockItem.metal === metalToDelete.name &&
          (stockItem.sold === "No" || stockItem.sold === false)
      );

      if (usedInUnsoldStock) {
        alert(
          `❌ Cannot delete "${metalToDelete.name}"\n\nThis metal type is currently being used by unsold stock items. Please sell or delete those stock items first.`
        );
        return;
      }

      // Check if it's used in any sold stock items (for information only)
      const usedInSoldStock = stock.some(
        (stockItem) =>
          stockItem.metal === metalToDelete.name &&
          (stockItem.sold === "Yes" || stockItem.sold === true)
      );

      const confirmMessage = usedInSoldStock
        ? `This metal type "${metalToDelete.name}" has been used in sold items. Are you sure you want to delete it?`
        : `Are you sure you want to delete "${metalToDelete.name}"?`;

      if (!window.confirm(confirmMessage)) {
        return;
      }

      const updatedMetals = metalTypes.filter((metal) => metal.id !== id);
      const result = await updateCompanyMasterData({ metals: updatedMetals });

      if (result.success) {
        setMetalTypes(updatedMetals);
        alert("✅ Metal type deleted successfully");
      } else {
        alert("❌ Failed to delete metal type: " + result.message);
      }
    },
    [metalTypes, updateCompanyMasterData, stock]
  );

  // ========= SILVER TYPE FUNCTIONS =========
  const handleAddSilverType = useCallback(async () => {
    if (!newSilverType.trim()) {
      alert("Please enter silver type name");
      return;
    }

    const newType = {
      id: Date.now().toString(),
      name: newSilverType.toLowerCase().replace(/\s+/g, ""),
      displayName: newSilverTypeDisplayName || toCapitalCase(newSilverType),
      shortcut: newSilverTypeShortcut
        ? toUpperCase(newSilverTypeShortcut).slice(0, 3)
        : newSilverType.substring(0, 3).toUpperCase(),
    };

    const updatedSilverTypes = [...silverTypes, newType];
    const result = await updateCompanyMasterData({
      silverTypes: updatedSilverTypes,
    });

    if (result.success) {
      setSilverTypes(updatedSilverTypes);
      setNewSilverType("");
      setNewSilverTypeDisplayName("");
      setNewSilverTypeShortcut("");
      setSilverTypeDialogOpen(false);
      alert("✅ Silver type added successfully");
    } else {
      alert("❌ Failed to add silver type: " + result.message);
    }
  }, [
    newSilverType,
    newSilverTypeDisplayName,
    newSilverTypeShortcut,
    silverTypes,
    updateCompanyMasterData,
    toCapitalCase,
    toUpperCase,
  ]);

  const handleUpdateSilverType = useCallback(async () => {
    if (!newSilverType.trim() || !editingSilverType) {
      alert("Please enter silver type name");
      return;
    }

    const updatedSilverTypes = silverTypes.map((type) =>
      type.id === editingSilverType.id
        ? {
          ...type,
          name: newSilverType.toLowerCase().replace(/\s+/g, ""),
          displayName:
            newSilverTypeDisplayName || toCapitalCase(newSilverType),
          shortcut: newSilverTypeShortcut
            ? toUpperCase(newSilverTypeShortcut).slice(0, 3)
            : newSilverType.substring(0, 3).toUpperCase(),
        }
        : type
    );

    const result = await updateCompanyMasterData({
      silverTypes: updatedSilverTypes,
    });

    if (result.success) {
      setSilverTypes(updatedSilverTypes);
      setNewSilverType("");
      setNewSilverTypeDisplayName("");
      setNewSilverTypeShortcut("");
      setEditingSilverType(null);
      setSilverTypeDialogOpen(false);
      alert("✅ Silver type updated successfully");
    } else {
      alert("❌ Failed to update silver type: " + result.message);
    }
  }, [
    newSilverType,
    newSilverTypeDisplayName,
    newSilverTypeShortcut,
    editingSilverType,
    silverTypes,
    updateCompanyMasterData,
    toCapitalCase,
    toUpperCase,
  ]);

  const handleDeleteSilverType = useCallback(
    async (id) => {
      if (
        !window.confirm("Are you sure you want to delete this silver type?")
      ) {
        return;
      }

      const updatedSilverTypes = silverTypes.filter((type) => type.id !== id);
      const result = await updateCompanyMasterData({
        silverTypes: updatedSilverTypes,
      });

      if (result.success) {
        setSilverTypes(updatedSilverTypes);
        alert("✅ Silver type deleted successfully");
      } else {
        alert("❌ Failed to delete silver type: " + result.message);
      }
    },
    [silverTypes, updateCompanyMasterData]
  );

  // Modified validation schema for Gross Weight Product mode
  const getValidationSchema = (isGrossWeightMode) => {
    if (isGrossWeightMode) {
      return Yup.object({
        metal: Yup.string().required("Metal type is required"),
        silverType: Yup.string().when("metal", {
          is: "Silver",
          then: (schema) => schema.required("Silver type is required"),
          otherwise: (schema) => schema,
        }),
        weight: Yup.number()
          .required("Net weight is required")
          .min(0.001, "Must be at least 0.001g")
          .typeError("Please enter a valid number"),
      });
    } else {
      return Yup.object({
        supplierName: Yup.string(),
        supplierShortcut: Yup.string(),
        name: Yup.string().when("metal", {
          is: (metal) => metal !== "Silver",
          then: (schema) => schema.required("Item name is required"),
          otherwise: (schema) => schema,
        }),
        metal: Yup.string().required("Metal type is required"),
        miscitemName: Yup.string().when(["name", "metal"], {
          is: (name, metal) => name === "Miscellaneous" && metal !== "Silver",
          then: (schema) => schema.required("Item Name is required"),
          otherwise: (schema) => schema,
        }),
        imageUrl: Yup.string().url("Invalid image URL"),
        miscitemShortcut: Yup.string().when(["name", "metal"], {
          is: (name, metal) => name === "Miscellaneous" && metal !== "Silver",
          then: (schema) =>
            schema
              .required("Item Shortcut is required")
              .max(3, "Max 3 characters"),
          otherwise: (schema) => schema,
        }),
        melting: Yup.number().when("metal", {
          is: (metal) => metal !== "Silver",
          then: (schema) =>
            schema
              .required("Melting percentage is required")
              .min(0, "Must be positive")
              .max(100, "Cannot exceed 100%")
              .typeError("Please enter a valid percentage"),
          otherwise: (schema) => schema,
        }),
        weight: Yup.number()
          .required("Net weight is required")
          .min(0.001, "Must be at least 0.001g")
          .typeError("Please enter a valid number"),
        makingCharges: Yup.number()
          .min(0, "Cannot be negative")
          .typeError("Please enter a valid number"),
        serialNo: Yup.number().when("metal", {
          is: (metal) => metal !== "Silver",
          then: (schema) =>
            schema
              .required("Serial number is required")
              .integer("Must be whole number")
              .min(1, "Must be at least 1")
              .typeError("Please enter a valid number"),
          otherwise: (schema) => schema,
        }),
        wastage: Yup.number()
          .min(0, "Must be positive")
          .when("metal", {
            is: (metal) => ["Gold", "RoseGold", "WhiteGold"].includes(metal),
            then: (schema) =>
              schema
                .typeError("Please enter a valid number"),
            otherwise: (schema) => schema,
          }),
        labour: Yup.number().typeError("Please enter a valid number"),
        description: Yup.string(),
        huidCodes: Yup.array().of(Yup.string()),
        itemDetail: Yup.string(),
      });
    }
  };

  // Helper function to find first missing number in a sorted array
  const findFirstMissingNumber = useCallback((arr) => {
    if (arr.length === 0) return 1;

    const numberSet = new Set(arr);
    let i = 1;

    while (numberSet.has(i)) {
      i++;
    }
    return i;
  }, []);

  // Get next serial number function
  const getNextSerialNumber = useCallback(
    (itemName, metal) => {
      if (!itemName || !metal) return 1;

      const allItemsForType = stock.filter(
        (item) => item.name === itemName && item.metal === metal
      );

      if (allItemsForType.length === 0) {
        return 1;
      }

      const unsoldItems = allItemsForType.filter(
        (item) => item.sold === "No" || item.sold === false
      );
      const unsoldSerialNumbers = unsoldItems
        .map((item) => parseInt(item.serialNo) || 0)
        .filter((num) => !isNaN(num) && num > 0)
        .sort((a, b) => a - b);

      const firstMissing = findFirstMissingNumber(unsoldSerialNumbers);
      return firstMissing;
    },
    [stock, findFirstMissingNumber]
  );

  // // Calculate net weight
  // const calculateNetWeight = useCallback((gross, less) => {
  //   const grossNum = parseFloat(gross) || 0;
  //   const lessNum = parseFloat(less) || 0;
  //   const net = grossNum - lessNum;
  //   return net >= 0 ? net.toFixed(3) : "0.000";
  // }, []);

  // Calculate final price
  const calculateFinalPrice = useCallback((values) => {
    const {
      metal,
      melting,
      weight,
      goldCuttingRate,
      wastage,
      silverRate,
      labour,
    } = values;

    if (!metal || !weight || parseFloat(weight) <= 0) return 0;

    let purity = parseFloat(melting) || 0;
    if (purity === 91.6) purity = 92;

    let finalPrice = 0;

    if (["Gold", "RoseGold", "WhiteGold"].includes(metal)) {
      const rate = parseFloat(goldCuttingRate) || 0;
      const wastagePct = parseFloat(wastage) || 0;
      const weightNum = parseFloat(weight) || 0;
      finalPrice = (((purity + wastagePct) / 100) * rate * weightNum).toFixed(
        0
      );
    } else if (metal === "Silver") {
      if (
        purity === 99.9 ||
        purity === 999 ||
        purity === 92 ||
        purity === 925 ||
        purity === 92.5
      ) {
        purity = 100;
      }
      const weightNum = parseFloat(weight) || 0;
      const labourNum = parseFloat(labour) || 0;
      const silverRateNum = parseFloat(silverRate) || 0;
      const wastageNum = parseFloat(wastage) || 0;
      finalPrice = (
        ((purity + wastageNum) / 100) *
        weightNum *
        (silverRateNum + labourNum)
      ).toFixed(0);
    }

    return finalPrice;
  }, []);

  // Generate QR code image
  const generateQRCode = async (text) => {
    try {
      const qrDataUrl = await QRCode.toDataURL(text, {
        width: 256,
        margin: 1,
      });
      setQrImage(qrDataUrl);
    } catch (err) {
      console.error("Error generating QR code:", err);
    }
  };

  // Handle weight calculation
  // const handleWeightChange = useCallback(
  //   (values, setFieldValue) => {
  //     const netWeight = calculateNetWeight(
  //       values.grossWeight,
  //       values.lessWeight
  //     );
  //     setFieldValue("weight", netWeight);
  //   },
  //   [calculateNetWeight]
  // );

  // Handle serial number generation
  const handleItemChange = useCallback(
    (itemName, metal, setFieldValue) => {
      if (itemName && metal) {
        try {
          const nextSerial = getNextSerialNumber(itemName, metal);
          setFieldValue("serialNo", nextSerial);
        } catch (error) {
          console.error("Error getting serial number:", error);
        }
      }
    },
    [getNextSerialNumber]
  );

  // Update dynamic fields based on item selection
  const updateDynamicFields = useCallback((itemName) => {
    let fields = {};

    // Earring categories
    const earringCategories = [
      "Tops",
      "Jumka",
      "Bali",
      "Dul",
      "Basket Bali",
      "Sui Dhaaga",
      "Jala",
    ];

    switch (itemName) {
      case "Finger Ring":
      case "Bangles/Kada":
      case "Bracelet":
        fields = {
          type: "select",
          name: "itemDetail",
          label: "Category",
          options: [
            { value: "Ladies", label: "Ladies" },
            { value: "Gents", label: "Gents" },
            { value: "Baby", label: "Baby" },
          ],
        };
        break;
      case "Necklace":
        fields = {
          type: "select",
          name: "itemDetail",
          label: "Necklace Type",
          options: [
            { value: "Short", label: "Short" },
            { value: "Long", label: "Long" },
          ],
        };
        break;
      case "Chain":
      case "Mangalsutra":
        fields = {
          type: "number",
          name: "itemDetail",
          label: "Size (inches)",
          placeholder: "Size in inches",
        };
        break;
      case "Earrings":
        fields = {
          type: "select",
          name: "itemDetail",
          label: "Earring Category",
          options: earringCategories.map((cat) => ({ value: cat, label: cat })),
        };
        break;
      default:
        fields = {};
    }

    setDynamicFields(fields);
  }, []);

  // Check for duplicate items
  const checkForDuplicate = useCallback(
    (itemName, metal, serialNo) => {
      if (!stock || stock.length === 0) return false;

      return stock.some(
        (item) =>
          item.name === itemName &&
          item.metal === metal &&
          parseInt(item.serialNo) === parseInt(serialNo) &&
          (item.sold === "No" || item.sold === false)
      );
    },
    [stock]
  );

  // Optimized function to add item to context without full refresh
  const addItemToContext = useCallback(
    async (newItem) => {
      if (addStock && typeof addStock === "function") {
        return await addStock(newItem);
      }
      return { success: false, message: "Add function not available" };
    },
    [addStock]
  );

  // Modified handleSubmit for Gross Weight Product
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    console.log("Form values on submit:", values);
    console.log("Gross Weight Product mode:", isGrossWeightProduct);

    setIsSubmitting(true);

    // Handle Gross Weight Product submission
    if (isGrossWeightProduct) {
      if (values.metal === "Silver") {
        values.weight = parseFloat(values.weight).toFixed(3);
        const bulkResult = await bulkUpdateSilverStock([
          {
            silverType: values.silverType,
            netWeight: values.weight,
            operation: "add",
            description:
              "Gross Weight Product - " +
              (values?.supplierName || "Added via Add Item"),
          },
        ]);
        if (!bulkResult.success) {
          alert(
            "❌ Failed to update silver stock:\n\n" +
            (bulkResult.message || "Unknown error")
          );
          setIsSubmitting(false);
          setSubmitting(false);
          return;
        } else {
          console.log("Silver stock updated successfully:", bulkResult);
          alert("✅ Silver weight added to stock successfully!");

          // Reset form after successful submission
          const clearedValues = {
            supplierName: "",
            supplierShortcut: "",
            name: "",
            metal: "Gold",
            silverType: "",
            melting: "92",
            grossWeight: "",
            lessWeight: "",
            weight: "",
            silverRate: "",
            labour: "",
            goldCuttingRate: "",
            wastage: "",
            makingCharges: "",
            serialNo: "",
            description: "",
            huidCodes: [""],
            itemDetail: "",
            miscitemName: "",
            miscitemShortcut: "",
            imageUrl: "",
          };
          setPersistedFormValues(clearedValues);
          resetForm({ values: clearedValues });

          setIsSubmitting(false);
          setSubmitting(false);
          return;
        }
      }

      // For other metals in Gross Weight mode, just add with minimal data
      try {
        console.log(
          "Submitting Gross Weight Product:",
          values.metal,
          values.weight
        );
        const submissionData = [
          {
            silverType: values.metal === "Gold" ? "gold" : values.metal,
            netWeight: values.weight,
            operation: "add",
            description:
              "Gross Weight Product - " +
              (values?.supplierName || "Added via Add Item"),
          },
        ];

        const result = bulkUpdateSilverStock(submissionData);
        console.log("Gross Weight Product submission result:", result);
        if (result.success) {
          setLastAddedItem(submissionData);
          setShowQR(true);
          alert("✅ Gross Weight Product Added Successfully!");

          // Reset form
          const clearedValues = {
            supplierName: "",
            supplierShortcut: "",
            name: "",
            metal: "Gold",
            silverType: "",
            melting: "92",
            grossWeight: "",
            lessWeight: "",
            weight: "",
            silverRate: "",
            labour: "",
            goldCuttingRate: "",
            wastage: "",
            makingCharges: "",
            serialNo: "",
            description: "",
            huidCodes: [""],
            itemDetail: "",
            miscitemName: "",
            miscitemShortcut: "",
            imageUrl: "",
          };
          setPersistedFormValues(clearedValues);
          resetForm({ values: clearedValues });
        } else {
          throw new Error(
            result.message || "Failed to add gross weight product"
          );
        }
      } catch (error) {
        console.error("Error adding gross weight product:", error);
        alert(
          error.message ||
          "Failed to add gross weight product. Please try again."
        );
      } finally {
        setIsSubmitting(false);
        setSubmitting(false);
      }
      return;
    }

    try {
      // Check for duplicate before proceeding
      if (checkForDuplicate(values.name, values.metal, values.serialNo)) {
        alert(
          "❌ Duplicate Item Found!\n\nPlease change the Serial Number, Item Category, or Metal as an item with the same combination already exists."
        );
        setIsSubmitting(false);
        setSubmitting(false);
        return;
      }

      // Prepare data for submission
      const qrCode = `JWL${Date.now()}`;
      const submissionData = {
        ...values,
        huid: values.huidCodes.filter(Boolean).join(", "),
        price: calculateFinalPrice(values),
        qr: qrCode,
        sold: "No",
        imageUrl: values.imageUrl || "",
      };

      console.log("Submission data:", submissionData);

      // Remove unnecessary fields
      delete submissionData.huidCodes;

      // Normalize rate fields based on metal
      if (["Gold", "RoseGold", "WhiteGold"].includes(values.metal)) {
        submissionData.silverRate = "";
      } else {
        submissionData.goldCuttingRate = "";
        submissionData.huid = "";
      }


      // Add the item directly to context WITHOUT full refresh
      const result = await addItemToContext(submissionData);

      if (result.success) {
        // Generate QR code
        await generateQRCode(qrCode);
        setGeneratedQR(qrCode);
        setLastAddedItem(submissionData);
        setShowQR(true);

        // Update persisted form values with current values
        const updatedValues = {
          ...values,
        };

        // Update the persisted values
        setPersistedFormValues(updatedValues);

        // Update dynamic fields based on kept item selection
        updateDynamicFields(values.name);

        alert("✅ Item Added & QR Generated Successfully!");
      } else {
        throw new Error(result.message || "Failed to add item");
      }
    } catch (error) {
      console.error("Error adding item:", error);
      alert(error.message || "Failed to add item. Please try again.");
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  // Handle print label function
  const handlePrintLabel = (item) => {
    console.log("=== PRINT LABEL DEBUG ===");
    console.log("Item:", item);
    console.log("Item.qr:", item?.qr);
    console.log("Item.serialNo:", item?.serialNo);
    console.log("Item.miscitemName:", item?.miscitemName);
    console.log("Item.miscitemShortcut:", item?.miscitemShortcut);
    console.log("Item.name:", item?.name);

    if (!item?.qr) {
      console.warn("No QR code data found for item");
      alert("QR code data not found for this item");
      return;
    }

    const printWindow = window.open("", "_blank");

    // Format the data for the label
    const formatNumber = (num) => {
      if (!num || num === "" || Number.isNaN(num) || num === "NaN") return "0";
      return parseFloat(num).toFixed(3);
    };

    // Get metal short code from metalTypes array
    const getMetalShort = (metal) => {
      if (!metal) return "G";
      const found = metalTypes.find((m) => m.name === metal);
      return found ? found.shortcut : "G";
    };

    // Get item type short code from itemCategories array
    const getItemType = (name) => {
      if (!name) return "";

      // First check if it's a Miscellaneous item
      if (name === "miscellaneous" && item.miscitemShortcut) {
        return item.miscitemShortcut;
      }

      // Look up in itemCategories
      const found = itemCategories.find(
        (cat) => cat.name.toLowerCase() === name.toLowerCase()
      );

      if (found) {
        return found.shortcut;
      }

      // Fallback for dynamic items not in the list
      return name.substring(0, 3).toUpperCase();
    };

    function wastageToAlphanumeric(wastage) {
      if (!wastage || wastage === "0" || wastage === "0.0") return "";

      const num = parseFloat(wastage);
      if (isNaN(num)) return "";

      // Handle whole numbers
      if (Number.isInteger(num)) {
        if (num <= 26) {
          // A-Z for 1-26
          return String.fromCharCode(64 + num);
        } else {
          // For numbers > 26, use number as is
          return num.toString();
        }
      }

      // Handle decimal numbers
      const wholePart = Math.floor(num);
      const decimalPart = Math.round((num - wholePart) * 100); // Get two decimal digits

      if (wholePart === 0) {
        // Just decimal part (e.g., 0.9 -> "9")
        return decimalPart.toString();
      }

      if (wholePart <= 26) {
        const letter = String.fromCharCode(64 + wholePart);
        if (decimalPart > 0) {
          return letter + decimalPart.toString();
        }
        return letter;
      } else {
        // For whole part > 26, use number + decimal
        if (decimalPart > 0) {
          return wholePart.toString() + decimalPart.toString();
        }
        return wholePart.toString();
      }
    }

    // Get purity from melting percentage
    const getPurity = (melting) => {
      if (!melting) return "92";
      const purity = Math.round((melting / 100) * 22);
      return purity.toString();
    };

    // Prepare data
    const formattedData = {
      serialNo: item.serialNo || "0",
      name: item.name || "",
      weight: formatNumber(item.weight) || "0",
      grossWeight: formatNumber(item.grossWeight) || "0",
      lessWeight: formatNumber(item.lessWeight) || "0",
      metal: getMetalShort(item.metal),
      huid: item.huid || "",
      makingCharges: item.makingCharges || "",
      wastage: wastageToAlphanumeric(item.wastage) || "0",
      purity: getPurity(item.melting),
      melting: item.melting,
      itemType: getItemType(item.name?.toLowerCase()),
      supplierShortcut: item?.supplierShortcut,
    };

    // Get the QR code directly from item.qr
    const qrData = item.qr || "";

    // Escape any quotes in the QR data for JavaScript
    const escapedQrData = qrData.replace(/"/g, '\\"').replace(/'/g, "\\'");

    const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Jewelry Label - ${item.serialNo}</title>
      <meta charset="UTF-8">
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        @page {
          size: 8.1cm 1.2cm; /* EXACT size of the label */
          margin: 0 !important; /* Force no margins */
          padding: 0 !important;
        }
        
        body {
          margin: 0 !important;
          padding: 0 !important;
          width: 8.1cm;
          height: 1.2cm;
          font-family: Arial, sans-serif;
          background: white;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          position: absolute;
          top: 0;
          left: 0;
          overflow: hidden;
        }
        
        .label-container {
          width: 8.1cm;
          height: 1.2cm;
          display: flex;
          position: absolute;
          top: 0;
          left: 0;
        }
        
        /* Dummy blank space (2.7 cm wide) */
        .blank-space {
          width: 2.7cm;
          height: 1.2cm;
        }
        
        /* Actual label content (5.4 cm × 1.2 cm) */
        .label {
          width: 5.4cm;
          height: 1.2cm;
          font-family: Arial, sans-serif;
          font-size: 7px;
          display: flex;
          box-sizing: border-box;
          padding: 0;
          position: relative;
        }
        
        .left,
        .right {
          width: 2.7cm;
          height: 1.2cm;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          box-sizing: border-box;
          padding-top: 9px !important;
        }
        
        .left {
          padding-left: 3px;
          text-align: left;
        }
        
        .left-inner {
          display: flex;
          flex-direction: row;
          align-items: center;
          width: 100%;
          height: 100%;
        }
        
        .qr-side {
          flex: 0 0 35%;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        
        .text-side {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          padding-left: 2px;
        }
        
        .qr {
          width: 1cm;
          height: 1cm;
          background: #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #999;
          font-size: 5px;
          overflow: hidden;
        }
    
        #qr-canvas {
          width: 100% !important;
          height: 100% !important;
          display: block;
        }
        
        .qr-text {
          font-size: 5px;
          text-align: center;
          line-height: 1;
        }
        
        .shop-name {
          font-size: 9px;
          font-weight: 900;
          color: black;
          text-align: center;
          width: 100%;
          line-height: 1.1;
        }
        
        .making {
          text-align: center;
          width: 100%;
          font-weight: 700;
          font-size: 8px;
          margin-top: 1px;
        }
        
        .item-code {
          font-size: 8px;
          font-weight: 750;
        }
        
        .right {
          padding-left: 8px;
          padding-top: 2px;
        }
        
        .details {
          font-size: 8px;
          font-weight: 750;
        }
        
        .weight {
          font-size: 8px;
          font-weight: 750;
        }
                
        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 8.1cm;
            height: 1.2cm;
          }
          
          body * {
            visibility: visible !important;
          }
          
          .label-container {
            position: absolute;
            top: 0;
            left: 0;
          }
          
          .label {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      </style>
      <!-- Load QRious library -->
      <script src="https://cdnjs.cloudflare.com/ajax/libs/qrious/4.0.2/qrious.min.js"></script>
    </head>
    <body>
      <div class="label-container">
        <!-- Dummy blank space (2.7 cm wide) -->
        <div class="blank-space"></div>
        
        <!-- Actual label content (5.4 cm × 1.2 cm) -->
        <div class="label">
          <!-- Left part -->
          <div class="left">
            <div class="left-inner">
              <!-- Left side: QR -->
              <div class="qr-side">
                <div id="pl-qr" class="qr">
                  <div class="qr-text">Generating QR...</div>
                </div>
              </div>
              <!-- Right side: Text -->
              <div class="text-side">
                <div class="shop-name">Bhairav Gold</div>
              </div>
            </div>
          </div>
          
          <!-- Right part -->
          <div class="right">
            <div class="details">
              <span id="itemType">${formattedData.itemType}</span> 
              <span id="itemSrNo">${formattedData.serialNo}</span>
              <span id="itemMetal"> | ${formattedData.metal}</span>
              <span id="itemPurity"> | ${formattedData.melting}</span>
            </div>
            <div class="weight">
              <span id="grossWeightSpan">GW-<span id="itemGrossWeight">${formattedData.grossWeight}</span></span>
              <span id="lessWeightSpan">LW-<span id="itemLessWeight">${formattedData.lessWeight}</span></span>
            </div>
            <div class="weight">NW - <span id="itemWeight">${formattedData.weight}</span></div>
            <div class="item-code">
              <span id="itemHuid">${formattedData.huid}</span> 
            </div>
          </div>
        </div>
      </div>
    
      <script>
        // QR code data
        const qrData = "${escapedQrData}";
        
        console.log('QR Data received:', qrData);
        
        if (!qrData || qrData.trim() === '') {
          console.error('No QR data provided');
          document.getElementById('pl-qr').innerHTML = '<div class="qr-text">NO QR DATA</div>';
          setTimeout(printDocument, 500);
        } else {
          function generateQRCode() {
            console.log('Generating QR code using QRious for:', qrData);
            
            const qrContainer = document.getElementById('pl-qr');
            
            try {
              qrContainer.innerHTML = '';
              
              const canvas = document.createElement('canvas');
              canvas.id = 'qr-canvas';
              qrContainer.appendChild(canvas);
              
              if (typeof QRious === 'undefined') {
                throw new Error('QRious library not loaded');
              }
              
              const qr = new QRious({
                element: canvas,
                value: qrData,
                size: 180,
                level: 'H',
                background: 'white',
                foreground: 'black',
                backgroundAlpha: 1,
                foregroundAlpha: 1,
                padding: 2
              });
              
              console.log('QR Code generated successfully');
              
              canvas.style.width = '100%';
              canvas.style.height = '100%';
              
              setTimeout(printDocument, 500);
              
            } catch (error) {
              console.error('QR generation failed:', error);
              qrContainer.innerHTML = '<div class="qr-text" style="font-size: 5px;">QR: ' + 
                                      qrData.substring(0, 8) + 
                                      (qrData.length > 8 ? '...' : '') + '</div>';
              setTimeout(printDocument, 500);
            }
          }
          
          function generateSimpleQRCode() {
            const qrContainer = document.getElementById('pl-qr');
            qrContainer.innerHTML = '';
            
            const canvas = document.createElement('canvas');
            canvas.width = 60;
            canvas.height = 60;
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            
            const ctx = canvas.getContext('2d');
            
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, 60, 60);
            
            ctx.fillStyle = '#000000';
            
            ctx.fillRect(4, 4, 10, 10);
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(6, 6, 6, 6);
            ctx.fillStyle = '#000000';
            
            ctx.fillRect(46, 4, 10, 10);
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(48, 6, 6, 6);
            ctx.fillStyle = '#000000';
            
            ctx.fillRect(4, 46, 10, 10);
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(6, 48, 6, 6);
            ctx.fillStyle = '#000000';
            
            ctx.fillStyle = '#000000';
            ctx.font = '6px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('QR', 30, 26);
            ctx.fillText('Code', 30, 34);
            
            qrContainer.appendChild(canvas);
            console.log('Simple QR placeholder created');
          }
          
          function printDocument() {
            console.log('Printing document...');
            
            // Force print settings
            const printSettings = {
              copies: 1,
              pageSize: 'Custom',
              margin: 'none'
            };
            
            window.print();
            
            setTimeout(() => {
              window.close();
            }, 1000);
          }
          
          function init() {
            if (typeof QRious !== 'undefined') {
              console.log('QRious library is loaded');
              generateQRCode();
            } else {
              console.log('Waiting for QRious library to load...');
              let attempts = 0;
              const maxAttempts = 10;
              
              function checkLibrary() {
                attempts++;
                if (typeof QRious !== 'undefined') {
                  console.log('QRious loaded after', attempts, 'attempts');
                  generateQRCode();
                } else if (attempts < maxAttempts) {
                  setTimeout(checkLibrary, 100);
                } else {
                  console.error('QRious library failed to load');
                  generateSimpleQRCode();
                  setTimeout(printDocument, 500);
                }
              }
              
              checkLibrary();
            }
          }
          
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
          } else {
            init();
          }
        }
      </script>
    </body>
    </html>
  `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    // Force the print dialog to use the correct paper size
    printWindow.focus();
  };

  // StyledSelect component
  const StyledSelect = ({ label, children, ...props }) => {
    return (
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel sx={{ color: isDark ? "#cbd5e1" : "#64748b" }}>
          {label}
        </InputLabel>
        <Select
          {...props}
          label={label}
          sx={{
            borderRadius: 2,
            backdropFilter: "blur(10px)",
            width: "250px",
            backgroundColor: alpha(
              isDark ? theme.palette.background.paper : "#ffffff",
              isDark ? 0.3 : 0.8
            ),
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(
                isDark ? theme.palette.divider : "#e2e8f0",
                isDark ? 0.2 : 0.5
              ),
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(
                isDark ? theme.palette.divider : "#e2e8f0",
                isDark ? 0.3 : 0.7
              ),
            },
          }}
        >
          {children}
        </Select>
      </FormControl>
    );
  };

  // Reset form when toggling Gross Weight mode
  const handleGrossWeightToggle = () => {
    setIsGrossWeightProduct(!isGrossWeightProduct);
    // Reset form values when toggling
    const clearedValues = {
      supplierName: "",
      supplierShortcut: "",
      name: "",
      metal: "Gold",
      silverType: "",
      melting: "92",
      grossWeight: "",
      lessWeight: "",
      weight: "",
      silverRate: "",
      labour: "",
      goldCuttingRate: "",
      wastage: "",
      makingCharges: "",
      serialNo: "",
      description: "",
      huidCodes: [""],
      itemDetail: "",
      miscitemName: "",
      miscitemShortcut: "",
      imageUrl: "",
    };
    setPersistedFormValues(clearedValues);
    setDynamicFields({});
    setShowQR(false);
    setLastAddedItem(null);
  };

  if (initialLoading || loading) {
    return <LoadingShimmer />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 4,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: isDark
            ? "radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)"
            : "radial-gradient(circle at 20% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.08) 0%, transparent 50%)",
          zIndex: 0,
        },
      }}
    >
      {/* Backdrop for submitting state */}
      <Backdrop
        open={isSubmitting}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          color: "#fff",
          backdropFilter: "blur(8px)",
          backgroundColor: alpha(isDark ? "#0f172a" : "#ffffff", 0.8),
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            p: 4,
            borderRadius: 3,
            backdropFilter: "blur(20px)",
            background: alpha(
              isDark ? theme.palette.background.paper : "#ffffff",
              isDark ? 0.3 : 0.8
            ),
            border: `1px solid ${alpha(
              isDark ? theme.palette.divider : "#e2e8f0",
              isDark ? 0.2 : 0.3
            )}`,
            boxShadow: theme.shadows[6],
          }}
        >
          <CircularProgress
            sx={{
              color: isDark ? "#60a5fa" : "#10b981",
              mb: 2,
            }}
          />
          <Typography
            sx={{
              fontWeight: 600,
              color: isDark ? "#e2e8f0" : "#2d3748",
            }}
          >
            {isGrossWeightProduct
              ? "Adding Gross Weight Product..."
              : "Adding Item..."}
          </Typography>
        </Box>
      </Backdrop>

      {/* Loading overlay for master data updates */}
      {isUpdatingMasterData && (
        <Backdrop
          open={true}
          sx={{
            zIndex: theme.zIndex.drawer + 2,
            backdropFilter: "blur(4px)",
            backgroundColor: alpha(isDark ? "#0f172a" : "#ffffff", 0.5),
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={40} />
            <Typography sx={{ mt: 2, color: isDark ? "#fff" : "#000" }}>
              Updating master data...
            </Typography>
          </Box>
        </Backdrop>
      )}

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <Card
          sx={{
            mb: 2,
            borderRadius: 2,
            overflow: "hidden",
            backdropFilter: "blur(20px)",
            background: alpha(theme.palette.background.paper, 0.4),
            border: `1px solid ${alpha(
              isDark ? theme.palette.divider : "#e2e8f0",
              isDark ? 0.2 : 0.3
            )}`,
            boxShadow: theme.shadows[4],
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "center",
                alignItems: { xs: "flex-start", md: "center" },
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    borderRadius: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AddIcon
                    sx={{
                      fontSize: 30,
                      color: "#475569",
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      background: isDark
                        ? "linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%)"
                        : "linear-gradient(90deg, #059669 0%, #10b981 100%)",
                      backgroundClip: "text",
                      letterSpacing: "-0.02em",
                      fontSize: { xs: "1rem", md: "1.5rem" },
                      color: isDark ? "#f1f5f9" : "#1e293b",
                    }}
                  >
                    Add New Jewellery Item
                  </Typography>
                  <Typography
                    sx={{
                      color: isDark ? "#cbd5e1" : "#64748b",
                      fontSize: "0.9rem",
                      fontWeight: 400,
                    }}
                  >
                    Enter item details to add to inventory
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Management Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<CategoryIcon />}
            onClick={() => {
              setEditingItem(null);
              setNewItemName("");
              setNewItemShortcut("");
              setItemDialogOpen(true);
            }}
            disabled={isUpdatingMasterData}
            sx={{
              borderRadius: 2,
              backdropFilter: "blur(10px)",
              background: alpha(theme.palette.background.paper, 0.3),
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            }}
          >
            Manage Items ({itemCategories.length})
          </Button>
          <Button
            variant="outlined"
            startIcon={<DiamondIcon />}
            onClick={() => {
              setEditingMetal(null);
              setNewMetalName("");
              setNewMetalShortcut("");
              setMetalDialogOpen(true);
            }}
            disabled={isUpdatingMasterData}
            sx={{
              borderRadius: 2,
              backdropFilter: "blur(10px)",
              background: alpha(theme.palette.background.paper, 0.3),
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            }}
          >
            Manage Metals ({metalTypes.length})
          </Button>
          {/* <Button
            variant="outlined"
            startIcon={<ScaleIcon />}
            onClick={() => {
              setEditingSilverType(null);
              setNewSilverType('');
              setNewSilverTypeDisplayName('');
              setNewSilverTypeShortcut('');
              setSilverTypeDialogOpen(true);
            }}
            disabled={isUpdatingMasterData}
            sx={{
              borderRadius: 2,
              backdropFilter: 'blur(10px)',
              background: alpha(theme.palette.background.paper, 0.3),
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            }}
          >
            Manage Silver Types ({silverTypes.length})
          </Button> */}
        </Box>

        {/* Gross Weight Product Toggle */}
        <GrossWeightToggle
          isDark={isDark}
          elevation={0}
          sx={{ borderRadius: 2 }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={isGrossWeightProduct}
                onChange={handleGrossWeightToggle}
                color="primary"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#8B5CF6",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#8B5CF6",
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontWeight: 600,
                  color: isDark ? "#e2e8f0" : "#475569",
                  fontSize: "1.1rem",
                }}
              >
                {isGrossWeightProduct
                  ? "💎 Gross Weight Product Mode (Minimum Details)"
                  : "📋 Regular Product Mode (All Details)"}
              </Typography>
            }
          />
          <Typography
            variant="caption"
            sx={{
              color: isDark ? "#94a3b8" : "#64748b",
              display: "block",
              mt: 1,
            }}
          >
            {isGrossWeightProduct
              ? "In this mode, you only need to select metal type and enter net weight. Perfect for bulk weight entries."
              : "Regular mode requires all item details for proper inventory tracking."}
          </Typography>
        </GrossWeightToggle>

        <Grid container spacing={3}>
          {/* Form Section */}
          <Grid item xs={12} md={8} sx={{ margin: "auto" }}>
            <Formik
              initialValues={initialValues}
              validationSchema={getValidationSchema(isGrossWeightProduct)}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {({
                values,
                errors,
                touched,
                setFieldValue,
                isSubmitting: formikSubmitting,
                resetForm,
              }) => {
                const updateNetWeight = (gross, less) => {
                  const grossNum = parseFloat(gross) || 0;
                  const lessNum = parseFloat(less) || 0;
                  const net = grossNum - lessNum;
                  setFieldValue("weight", net >= 0 ? net.toFixed(3) : "0.000");
                };

                // Helper function to format weight to 3 decimals
                const formatWeightValue = (val) => {
                  if (!val || val === '') return '';
                  const parsed = parseFloat(val);
                  return !isNaN(parsed) ? parsed.toFixed(3) : '';
                };

                return (
                  <Form>
                    {/* Show only minimal fields in Gross Weight mode */}
                    {isGrossWeightProduct ? (
                      <>
                        {/* Metal and Weight Only */}
                        <Card
                          sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            backdropFilter: "blur(20px)",
                            background: alpha(
                              theme.palette.background.paper,
                              0.5
                            ),
                            border: `1px solid ${alpha(
                              isDark ? theme.palette.divider : "#e2e8f0",
                              isDark ? 0.2 : 0.3
                            )}`,
                            boxShadow: theme.shadows[4],
                            mb: 3,
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Typography
                              variant="h5"
                              sx={{
                                mb: 3,
                                fontWeight: 700,
                                color: isDark ? "#e2e8f0" : "#475569",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <ScaleIcon fontSize="small" />
                              Gross Weight Product Details
                            </Typography>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <FormControl
                                  fullWidth
                                  size="small"
                                  sx={{ mb: 2 }}
                                >
                                  <InputLabel>Metal Type *</InputLabel>
                                  <Select
                                    value={values.metal}
                                    onChange={(e) =>
                                      setFieldValue("metal", e.target.value)
                                    }
                                    label="Metal Type *"
                                    sx={{
                                      borderRadius: 2,
                                      backdropFilter: "blur(10px)",
                                      width: "250px",
                                    }}
                                  >
                                    <MenuItem value="">Select Metal</MenuItem>
                                    {metalTypes.map((metal) => (
                                      <MenuItem key={metal.id} value={metal.name}>
                                        {metal.name} ({metal.shortcut})
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              {values.metal === "Silver" && (
                                <Grid item xs={12} md={6}>
                                  <StyledSelect
                                    label="Silver Type *"
                                    name="silverType"
                                    value={values.silverType}
                                    onChange={(e) =>
                                      setFieldValue("silverType", e.target.value)
                                    }
                                    error={
                                      touched.silverType &&
                                      Boolean(errors.silverType)
                                    }
                                  >
                                    <MenuItem value="">
                                      Select Silver Type
                                    </MenuItem>
                                    {silverTypes.map((type) => (
                                      <MenuItem key={type.id} value={type.name}>
                                        {type.displayName || type.name}
                                      </MenuItem>
                                    ))}
                                  </StyledSelect>
                                </Grid>
                              )}

                              <Grid
                                item
                                xs={12}
                                md={values.metal === "Silver" ? 12 : 6}
                              >
                                <StyledTextField
                                  fullWidth
                                  label="Net Weight (g) *"
                                  name="weight"
                                  type="text"
                                  value={values.weight}
                                  onChange={(e) =>
                                    setFieldValue("weight", e.target.value)
                                  }
                                  error={touched.weight && Boolean(errors.weight)}
                                  helperText={touched.weight && errors.weight}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        g
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                            </Grid>

                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mt: 2,
                                color: isDark ? "#94a3b8" : "#64748b",
                                fontStyle: "italic",
                              }}
                            >
                              Note: This mode only records metal type and weight
                              for inventory tracking.
                            </Typography>
                          </CardContent>
                        </Card>
                      </>
                    ) : (
                      <>
                        {/* Supplier Information */}
                        <Card
                          sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            backdropFilter: "blur(20px)",
                            background: alpha(
                              theme.palette.background.paper,
                              0.5
                            ),
                            border: `1px solid ${alpha(
                              isDark ? theme.palette.divider : "#e2e8f0",
                              isDark ? 0.2 : 0.3
                            )}`,
                            boxShadow: theme.shadows[4],
                            mb: 3,
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Typography
                              variant="h5"
                              sx={{
                                mb: 3,
                                fontWeight: 700,
                                color: isDark ? "#e2e8f0" : "#475569",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <PersonIcon fontSize="small" />
                              Supplier Information
                            </Typography>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Autocomplete
                                  freeSolo
                                  key={`supplier-${values.supplierName || 'empty'}`}
                                  options={supplierOptions}
                                  loading={loadingSuppliers}
                                  getOptionLabel={(option) => {
                                    if (typeof option === "string") {
                                      return toCapitalCase(option);
                                    }
                                    return toCapitalCase(option.name) || "";
                                  }}
                                  value={values.supplierName || null}
                                  onChange={(event, newValue) => {
                                    if (typeof newValue === "string") {
                                      const formattedValue =
                                        toCapitalCase(newValue);
                                      setFieldValue(
                                        "supplierName",
                                        formattedValue
                                      );
                                      setFieldValue("supplierShortcut", "");
                                    } else if (newValue && newValue.name) {
                                      const formattedValue = toCapitalCase(
                                        newValue.name
                                      );
                                      setFieldValue(
                                        "supplierName",
                                        formattedValue
                                      );
                                      if (newValue.shortcut) {
                                        setFieldValue(
                                          "supplierShortcut",
                                          newValue.shortcut.toUpperCase()
                                        );
                                      }
                                    } else {
                                      setFieldValue("supplierName", "");
                                      setFieldValue("supplierShortcut", "");
                                    }
                                  }}
                                  onInputChange={(event, newInputValue) => {
                                    setFieldValue(
                                      "supplierName",
                                      toCapitalCase(newInputValue)
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <StyledTextField
                                      {...params}
                                      fullWidth
                                      label="Supplier Name"
                                      name="supplierName"
                                      value={values.supplierName}
                                      error={
                                        touched.supplierName &&
                                        Boolean(errors.supplierName)
                                      }
                                      helperText={
                                        touched.supplierName &&
                                        errors.supplierName
                                      }
                                      sx={{
                                        width: "250px",
                                      }}
                                      InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <PersonIcon
                                              sx={{
                                                color: isDark
                                                  ? "#94a3b8"
                                                  : "#64748b",
                                              }}
                                            />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                  renderOption={(props, option) => (
                                    <li
                                      {...props}
                                      style={{ padding: "8px 16px" }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                        }}
                                      >
                                        <Avatar
                                          sx={{
                                            bgcolor: alpha("#8B5CF6", 0.1),
                                            color: "#8B5CF6",
                                            fontWeight: 600,
                                            border: `1.5px solid ${alpha(
                                              "#8B5CF6",
                                              0.3
                                            )}`,
                                          }}
                                        >
                                          {option.name.charAt(0)}
                                        </Avatar>
                                        <Box>
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              fontWeight: 600,
                                              color: isDark
                                                ? "#F1F5F9"
                                                : "#1E293B",
                                            }}
                                          >
                                            {toCapitalCase(option.name)}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: isDark
                                                ? "#94A3B8"
                                                : "#64748B",
                                            }}
                                          >
                                            {option.shortcut &&
                                              `Shortcut: ${option.shortcut}`}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </li>
                                  )}
                                  filterOptions={(options, state) => {
                                    const inputValue =
                                      state.inputValue.toLowerCase();
                                    if (!inputValue) return options;

                                    return options.filter((option) => {
                                      const optionName = (
                                        option.name || ""
                                      ).toLowerCase();
                                      return optionName.includes(inputValue);
                                    });
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <StyledTextField
                                  fullWidth
                                  label="Supplier Shortcut"
                                  name="supplierShortcut"
                                  value={values.supplierShortcut}
                                  onChange={(e) => {
                                    const value = e.target.value.toUpperCase();
                                    setFieldValue("supplierShortcut", value);
                                  }}
                                  error={
                                    touched.supplierShortcut &&
                                    Boolean(errors.supplierShortcut)
                                  }
                                  helperText={
                                    touched.supplierShortcut &&
                                    errors.supplierShortcut
                                  }
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <ShortTextIcon
                                          sx={{
                                            color: isDark ? "#94a3b8" : "#64748b",
                                          }}
                                        />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>

                        {/* Item Details Card */}
                        <Card
                          sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            backdropFilter: "blur(20px)",
                            background: alpha(
                              theme.palette.background.paper,
                              0.5
                            ),
                            border: `1px solid ${alpha(
                              isDark ? theme.palette.divider : "#e2e8f0",
                              isDark ? 0.2 : 0.3
                            )}`,
                            boxShadow: theme.shadows[4],
                            mb: 3,
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Typography
                              variant="h5"
                              sx={{
                                mb: 3,
                                fontWeight: 700,
                                color: isDark ? "#e2e8f0" : "#475569",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CategoryIcon fontSize="small" />
                              Item Details
                            </Typography>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <FormControl
                                  fullWidth
                                  size="small"
                                  sx={{ mb: 2 }}
                                >
                                  <InputLabel>Item Name *</InputLabel>
                                  <Select
                                    value={values.name}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setFieldValue("name", value);
                                      updateDynamicFields(value);
                                      handleItemChange(
                                        value,
                                        values.metal,
                                        setFieldValue
                                      );
                                    }}
                                    label="Item Name *"
                                    error={touched.name && Boolean(errors.name)}
                                    sx={{
                                      borderRadius: 2,
                                      backdropFilter: "blur(10px)",
                                      width: "250px",
                                    }}
                                  >
                                    <MenuItem value="">Select Item</MenuItem>
                                    {itemCategories.map((item) => (
                                      <MenuItem key={item.id} value={item.name}>
                                        {item.name} ({item.shortcut})
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <FormControl
                                  fullWidth
                                  size="small"
                                  sx={{ mb: 2 }}
                                >
                                  <InputLabel>Metal Type *</InputLabel>
                                  <Select
                                    value={values.metal || metalTypes[0]?.name || ""}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setFieldValue("metal", value);
                                      handleItemChange(
                                        values.name,
                                        value,
                                        setFieldValue
                                      );
                                    }}
                                    label="Metal Type *"
                                    error={touched.metal && Boolean(errors.metal)}
                                    sx={{
                                      borderRadius: 2,
                                      backdropFilter: "blur(10px)",
                                      width: "250px",
                                    }}
                                  >
                                    <MenuItem value="">Select Metal</MenuItem>
                                    {metalTypes.map((metal) => (
                                      <MenuItem key={metal.id} value={metal.name}>
                                        {metal.name} ({metal.shortcut})
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                            </Grid>

                            {values.name === "Miscellaneous" && (
                              <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} md={6}>
                                  <StyledTextField
                                    fullWidth
                                    label="Item Name"
                                    name="miscitemName"
                                    value={values.miscitemName}
                                    onChange={(e) => {
                                      const value = toCapitalCase(e.target.value);
                                      setFieldValue("miscitemName", value);
                                    }}
                                    error={
                                      touched.miscitemName &&
                                      Boolean(errors.miscitemName)
                                    }
                                    helperText={
                                      touched.miscitemName && errors.miscitemName
                                    }
                                    placeholder="Item Name"
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <ShortTextIcon
                                            sx={{
                                              color: isDark
                                                ? "#94a3b8"
                                                : "#64748b",
                                            }}
                                          />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <StyledTextField
                                    fullWidth
                                    label="Item Shortcut"
                                    name="miscitemShortcut"
                                    value={values.miscitemShortcut}
                                    onChange={(e) => {
                                      const value = toUpperCase(e.target.value);
                                      setFieldValue(
                                        "miscitemShortcut",
                                        value.slice(0, 3)
                                      );
                                    }}
                                    error={
                                      touched.miscitemShortcut &&
                                      Boolean(errors.miscitemShortcut)
                                    }
                                    helperText={
                                      touched.miscitemShortcut &&
                                      errors.miscitemShortcut
                                    }
                                    placeholder="Item Shortcut"
                                    inputProps={{
                                      maxLength: 3,
                                    }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <ShortTextIcon
                                            sx={{
                                              color: isDark
                                                ? "#94a3b8"
                                                : "#64748b",
                                            }}
                                          />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            )}

                            {/* Dynamic Fields */}
                            {dynamicFields.type && (
                              <Grid item xs={12}>
                                {dynamicFields.type === "select" ? (
                                  <StyledSelect
                                    label={dynamicFields.label}
                                    name={dynamicFields.name}
                                    value={values[dynamicFields.name] || ""}
                                    onChange={(e) =>
                                      setFieldValue(
                                        dynamicFields.name,
                                        e.target.value
                                      )
                                    }
                                  >
                                    <MenuItem value="">
                                      Select {dynamicFields.label}
                                    </MenuItem>
                                    {dynamicFields.options.map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </StyledSelect>
                                ) : (
                                  <StyledTextField
                                    fullWidth
                                    label={dynamicFields.label}
                                    name={dynamicFields.name}
                                    value={values[dynamicFields.name] || ""}
                                    onChange={(e) =>
                                      setFieldValue(
                                        dynamicFields.name,
                                        e.target.value
                                      )
                                    }
                                    placeholder={dynamicFields.placeholder}
                                    type="text"
                                  />
                                )}
                              </Grid>
                            )}
                          </CardContent>
                        </Card>

                        {/* Weight Details Card */}
                        <Card
                          sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            backdropFilter: "blur(20px)",
                            background: alpha(theme.palette.background.paper, 0.5),
                            border: `1px solid ${alpha(
                              isDark ? theme.palette.divider : "#e2e8f0",
                              isDark ? 0.2 : 0.3
                            )}`,
                            boxShadow: theme.shadows[4],
                            mb: 3,
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Typography
                              variant="h5"
                              sx={{
                                mb: 3,
                                fontWeight: 700,
                                color: isDark ? "#e2e8f0" : "#475569",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <ScaleIcon fontSize="small" />
                              Weight Details
                            </Typography>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <StyledTextField
                                  fullWidth
                                  label="Melting (%)"
                                  name="melting"
                                  type="text"
                                  value={values.melting}
                                  onChange={(e) => setFieldValue("melting", e.target.value)}
                                  onBlur={() => {
                                    if (values.melting) {
                                      calculateFinalPrice(values);
                                    }
                                  }}
                                  error={touched.melting && Boolean(errors.melting)}
                                  helperText={touched.melting && errors.melting}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <StyledTextField
                                  fullWidth
                                  label="Gross Weight (g)"
                                  name="grossWeight"
                                  type="text"
                                  value={values.grossWeight}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (!val) {
                                      setFieldValue("grossWeight", "");
                                      updateNetWeight("", values.lessWeight);
                                      return;
                                    }
                                    if (/^\d*\.?\d*$/.test(val)) {
                                      setFieldValue("grossWeight", val);
                                      updateNetWeight(val, values.lessWeight);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const formatted = formatWeightValue(e.target.value);
                                    if (formatted !== '') {
                                      setFieldValue("grossWeight", formatted);
                                    }
                                  }}
                                  error={touched.grossWeight && Boolean(errors.grossWeight)}
                                  helperText={touched.grossWeight && errors.grossWeight}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">g</InputAdornment>,
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <StyledTextField
                                  fullWidth
                                  label="Less Weight (g)"
                                  name="lessWeight"
                                  type="text"
                                  value={values.lessWeight}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (!val) {
                                      setFieldValue("lessWeight", "");
                                      updateNetWeight(values.grossWeight, "");
                                      return;
                                    }
                                    if (/^\d*\.?\d*$/.test(val)) {
                                      setFieldValue("lessWeight", val);
                                      updateNetWeight(values.grossWeight, val);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const formatted = formatWeightValue(e.target.value);
                                    if (formatted !== '') {
                                      setFieldValue("lessWeight", formatted);
                                    }
                                  }}
                                  error={touched.lessWeight && Boolean(errors.lessWeight)}
                                  helperText={touched.lessWeight && errors.lessWeight}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">g</InputAdornment>,
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <StyledTextField
                                  fullWidth
                                  label="Net Weight (g)"
                                  name="weight"
                                  type="text"
                                  value={values.weight}
                                  readOnly
                                  error={touched.weight && Boolean(errors.weight)}
                                  helperText={touched.weight && errors.weight}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">g</InputAdornment>,
                                    readOnly: true,
                                    sx: {
                                      backgroundColor: alpha(
                                        isDark ? theme.palette.background.paper : "#f8fafc",
                                        isDark ? 0.2 : 0.5
                                      ),
                                    },
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>

                        {/* Gold/Silver Specific Fields */}
                        <>
                          {["Gold", "RoseGold", "WhiteGold"].includes(
                            values.metal
                          ) ? (
                            <Card
                              sx={{
                                borderRadius: 3,
                                overflow: "hidden",
                                backdropFilter: "blur(20px)",
                                background: alpha(
                                  theme.palette.background.paper,
                                  0.5
                                ),
                                border: `1px solid ${alpha(
                                  isDark ? theme.palette.divider : "#e2e8f0",
                                  isDark ? 0.2 : 0.3
                                )}`,
                                boxShadow: theme.shadows[4],
                                mb: 3,
                              }}
                            >
                              <CardContent sx={{ p: 3 }}>
                                <Typography
                                  variant="h5"
                                  sx={{
                                    mb: 3,
                                    fontWeight: 700,
                                    color: isDark ? "#e2e8f0" : "#475569",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <DiamondIcon fontSize="small" />
                                  Gold Item Details
                                </Typography>

                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <StyledTextField
                                      fullWidth
                                      label="Gold Cutting Rate (₹)"
                                      name="goldCuttingRate"
                                      type="text"
                                      value={values.goldCuttingRate}
                                      onChange={(e) =>
                                        setFieldValue(
                                          "goldCuttingRate",
                                          e.target.value
                                        )
                                      }
                                      onBlur={() => calculateFinalPrice(values)}
                                      error={
                                        touched.goldCuttingRate &&
                                        Boolean(errors.goldCuttingRate)
                                      }
                                      helperText={
                                        touched.goldCuttingRate &&
                                        errors.goldCuttingRate
                                      }
                                    />
                                  </Grid>
                                </Grid>

                                {/* HUID Codes */}
                                <Box sx={{ mt: 3 }}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      mb: 2,
                                      fontWeight: 600,
                                      color: isDark ? "#e2e8f0" : "#475569",
                                    }}
                                  >
                                    HUID Codes
                                  </Typography>
                                  <FieldArray name="huidCodes">
                                    {({ push, remove, form }) => (
                                      <Box>
                                        {form.values.huidCodes.map((_, index) => (
                                          <Box
                                            key={index}
                                            sx={{
                                              display: "flex",
                                              gap: 1,
                                              mb: 1,
                                            }}
                                          >
                                            <StyledTextField
                                              fullWidth
                                              label={`HUID Code ${index + 1}`}
                                              name={`huidCodes[${index}]`}
                                              value={form.values.huidCodes[index]}
                                              onChange={(e) => {
                                                const value = toUpperCase(
                                                  e.target.value
                                                );
                                                setFieldValue(
                                                  `huidCodes[${index}]`,
                                                  value
                                                );
                                              }}
                                            />
                                            {index > 0 && (
                                              <IconButton
                                                onClick={() => remove(index)}
                                                sx={{
                                                  backdropFilter: "blur(10px)",
                                                  background: alpha(
                                                    theme.palette.error.main,
                                                    0.1
                                                  ),
                                                  border: `1px solid ${alpha(
                                                    theme.palette.error.main,
                                                    0.2
                                                  )}`,
                                                  color: theme.palette.error.main,
                                                  "&:hover": {
                                                    background: alpha(
                                                      theme.palette.error.main,
                                                      0.2
                                                    ),
                                                  },
                                                }}
                                              >
                                                <DeleteIcon />
                                              </IconButton>
                                            )}
                                          </Box>
                                        ))}
                                        <Button
                                          startIcon={<AddIcon />}
                                          onClick={() => push("")}
                                          sx={{
                                            borderRadius: 2,
                                            backdropFilter: "blur(10px)",
                                            background: alpha(
                                              isDark
                                                ? theme.palette.background.paper
                                                : "#ffffff",
                                              isDark ? 0.3 : 0.8
                                            ),
                                            border: `1px solid ${alpha(
                                              isDark
                                                ? theme.palette.divider
                                                : "#e2e8f0",
                                              isDark ? 0.2 : 0.3
                                            )}`,
                                            color: isDark ? "#e2e8f0" : "#475569",
                                          }}
                                        >
                                          Add HUID Code
                                        </Button>
                                      </Box>
                                    )}
                                  </FieldArray>
                                </Box>
                              </CardContent>
                            </Card>
                          ) : (
                            <Card
                              sx={{
                                borderRadius: 2,
                                overflow: "hidden",
                                backdropFilter: "blur(20px)",
                                background: alpha(
                                  theme.palette.background.paper,
                                  0.5
                                ),
                                border: `1px solid ${alpha(
                                  isDark ? theme.palette.divider : "#e2e8f0",
                                  isDark ? 0.2 : 0.3
                                )}`,
                                boxShadow: theme.shadows[4],
                                mb: 3,
                              }}
                            >
                              <CardContent sx={{ p: 3 }}>
                                <Typography
                                  variant="h5"
                                  sx={{
                                    mb: 3,
                                    fontWeight: 700,
                                    color: isDark ? "#e2e8f0" : "#475569",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <AttachMoneyIcon fontSize="small" />
                                  Other Metal Details
                                </Typography>

                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <StyledTextField
                                      fullWidth
                                      label="Silver Rate (₹)"
                                      name="silverRate"
                                      type="text"
                                      value={values.silverRate}
                                      onChange={(e) =>
                                        setFieldValue(
                                          "silverRate",
                                          e.target.value
                                        )
                                      }
                                      onBlur={() => calculateFinalPrice(values)}
                                      error={
                                        touched.silverRate &&
                                        Boolean(errors.silverRate)
                                      }
                                      helperText={
                                        touched.silverRate && errors.silverRate
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <StyledTextField
                                      fullWidth
                                      label="Labour (₹)"
                                      name="labour"
                                      type="text"
                                      value={values.labour}
                                      onChange={(e) =>
                                        setFieldValue("labour", e.target.value)
                                      }
                                      onBlur={() => calculateFinalPrice(values)}
                                      error={
                                        touched.labour && Boolean(errors.labour)
                                      }
                                      helperText={touched.labour && errors.labour}
                                    />
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          )}
                          <Grid item xs={12} md={6}>
                            <StyledTextField
                              fullWidth
                              label="Wastage (%)"
                              name="wastage"
                              type="text"
                              value={values.wastage}
                              onChange={(e) =>
                                setFieldValue("wastage", e.target.value)
                              }
                              onBlur={() => calculateFinalPrice(values)}
                              error={touched.wastage && Boolean(errors.wastage)}
                              helperText={touched.wastage && errors.wastage}
                              sx={{ mb: 3 }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    %
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>

                          <Card
                            sx={{
                              borderRadius: 2,
                              overflow: "hidden",
                              backdropFilter: "blur(20px)",
                              background: alpha(
                                theme.palette.background.paper,
                                0.5
                              ),
                              border: `1px solid ${alpha(
                                isDark ? theme.palette.divider : "#e2e8f0",
                                isDark ? 0.2 : 0.3
                              )}`,
                              boxShadow: theme.shadows[4],
                              mb: 3,
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Typography
                                variant="h5"
                                sx={{
                                  mb: 3,
                                  fontWeight: 700,
                                  color: isDark ? "#e2e8f0" : "#475569",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <TagIcon fontSize="small" />
                                Additional Information
                              </Typography>

                              {/* Add this inside the Additional Information Card, before or after other fields */}
                              <Grid item xs={12}>
                                <ImageUploadField
                                  key={`image-upload-${resetKey}`}
                                  value={values.imageUrl}
                                  onChange={(url, publicId) => {
                                    setFieldValue("imageUrl", url);
                                    // Also store publicId if needed for deletion later
                                    setFieldValue("imagePublicId", publicId);
                                  }}
                                  onRemove={() => {
                                    setFieldValue("imageUrl", "");
                                    setFieldValue("imagePublicId", "");
                                  }}
                                  cloudName="dhllj9fhm" // Replace with your actual cloud name
                                  uploadPreset="bhairav_gold" // Replace with your actual upload preset
                                  label="Product Image"
                                />
                              </Grid>

                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <StyledTextField
                                    fullWidth
                                    label="Making Charges (%)"
                                    name="makingCharges"
                                    type="text"
                                    value={values.makingCharges}
                                    onChange={(e) =>
                                      setFieldValue(
                                        "makingCharges",
                                        e.target.value
                                      )
                                    }
                                    error={
                                      touched.makingCharges &&
                                      Boolean(errors.makingCharges)
                                    }
                                    helperText={
                                      touched.makingCharges &&
                                      errors.makingCharges
                                    }
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          %
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <StyledTextField
                                    fullWidth
                                    label="Serial No."
                                    name="serialNo"
                                    type="text"
                                    value={values.serialNo}
                                    readOnly
                                    error={
                                      touched.serialNo && Boolean(errors.serialNo)
                                    }
                                    helperText={
                                      touched.serialNo && errors.serialNo
                                    }
                                    InputProps={{
                                      readOnly: true,
                                      sx: {
                                        backgroundColor: alpha(
                                          isDark
                                            ? theme.palette.background.paper
                                            : "#f8fafc",
                                          isDark ? 0.2 : 0.5
                                        ),
                                      },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <StyledTextField
                                    fullWidth
                                    label="Final Price (₹/100g)"
                                    value={
                                      calculateFinalPrice(values)
                                        ? `₹${calculateFinalPrice(values)}`
                                        : ""
                                    }
                                    readOnly
                                    InputProps={{
                                      readOnly: true,
                                      sx: {
                                        backgroundColor: alpha(
                                          isDark
                                            ? theme.palette.background.paper
                                            : "#f8fafc",
                                          isDark ? 0.2 : 0.5
                                        ),
                                        fontWeight: 600,
                                        color: isDark ? "#e2e8f0" : "#2d3748",
                                      },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <StyledTextField
                                    fullWidth
                                    label="Description (Optional)"
                                    name="description"
                                    value={values.description}
                                    onChange={(e) =>
                                      setFieldValue("description", e.target.value)
                                    }
                                    multiline
                                    rows={3}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <DescriptionIcon
                                            sx={{
                                              color: isDark
                                                ? "#94a3b8"
                                                : "#64748b",
                                            }}
                                          />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </>
                      </>
                    )}

                    {/* Action Buttons */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={
                          isSubmitting || formikSubmitting || isUpdatingMasterData
                        }
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress size={20} />
                          ) : (
                            <QrCodeIcon />
                          )
                        }
                        sx={{
                          borderRadius: 3,
                          fontWeight: 600,
                          textTransform: "none",
                          background: isDark
                            ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                            : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                          boxShadow: isDark
                            ? "0 4px 14px 0 rgba(59, 130, 246, 0.35)"
                            : "0 4px 14px 0 rgba(16, 185, 129, 0.35)",
                          backdropFilter: "blur(10px)",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            background: isDark
                              ? "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)"
                              : "linear-gradient(135deg, #059669 0%, #047857 100%)",
                            boxShadow: isDark
                              ? "0 6px 20px 0 rgba(59, 130, 246, 0.45)"
                              : "0 6px 20px 0 rgba(16, 185, 129, 0.45)",
                            transform: "translateY(-2px)",
                          },
                          "&.Mui-disabled": {
                            background: alpha(
                              isDark ? "#3b82f6" : "#10b981",
                              0.3
                            ),
                            boxShadow: "none",
                          },
                        }}
                      >
                        {isSubmitting
                          ? "Processing..."
                          : isGrossWeightProduct
                            ? "Add Gross Weight"
                            : "Add & Generate QR"}
                      </Button>

                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => {
                          const clearedValues = {
                            supplierName: "",
                            supplierShortcut: "",
                            name: "",
                            metal: "Gold",
                            melting: "92",
                            grossWeight: "",
                            lessWeight: "",
                            weight: "",
                            silverRate: "",
                            goldCuttingRate: "",
                            wastage: "",
                            makingCharges: "",
                            serialNo: "",
                            description: "",
                            huidCodes: [""],
                            itemDetail: "",
                            miscitemName: "",
                            miscitemShortcut: "",
                            imageUrl: "",
                          };
                          resetForm({ values: clearedValues });
                          setPersistedFormValues(clearedValues);
                          setDynamicFields({});
                          setShowQR(false);
                          setLastAddedItem(null);
                          resetForm({ values: clearedValues });
                          setResetKey(prev => prev + 1); // ADD THIS - increments counter to force remount
                        }}
                        disabled={isUpdatingMasterData}
                        sx={{
                          borderRadius: 3,
                          fontWeight: 600,
                          textTransform: "none",
                          backdropFilter: "blur(10px)",
                          background: alpha(
                            isDark ? theme.palette.background.paper : "#ffffff",
                            isDark ? 0.3 : 0.8
                          ),
                          color: isDark ? "#e2e8f0" : "#475569",
                          border: `1.5px solid ${alpha(
                            isDark ? theme.palette.divider : "#e2e8f0",
                            isDark ? 0.2 : 0.5
                          )}`,
                          px: 4,
                          py: 1.5,
                          "&:hover": {
                            background: alpha(
                              isDark ? theme.palette.background.paper : "#ffffff",
                              isDark ? 0.5 : 0.9
                            ),
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        Reset Form
                      </Button>
                    </Box>
                  </Form>
                )
              }}
            </Formik>
          </Grid>

          {/* QR Code Display Section */}
          <Grid item xs={12} md={4}>
            {showQR && qrImage && (
              <Card
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  backdropFilter: "blur(20px)",
                  background: alpha(theme.palette.background.paper, 0.7),
                  border: `1px solid ${alpha(
                    isDark ? theme.palette.divider : "#e2e8f0",
                    isDark ? 0.2 : 0.3
                  )}`,
                  boxShadow: theme.shadows[4],
                  position: "sticky",
                  top: 20,
                }}
              >
                <CardContent sx={{ p: 3, textAlign: "center" }}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 3,
                      fontWeight: 700,
                      color: isDark ? "#e2e8f0" : "#475569",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <QrCodeIcon fontSize="small" />
                    Generated QR Code
                  </Typography>

                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backdropFilter: "blur(10px)",
                      background: alpha(
                        isDark ? theme.palette.background.paper : "#ffffff",
                        isDark ? 0.3 : 0.8
                      ),
                      border: `1px solid ${alpha(
                        isDark ? theme.palette.divider : "#e2e8f0",
                        isDark ? 0.2 : 0.3
                      )}`,
                      mb: 3,
                    }}
                  >
                    <img
                      src={qrImage}
                      alt="QR Code"
                      style={{
                        width: "100%",
                        maxWidth: "256px",
                        height: "auto",
                        borderRadius: "8px",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 1,
                        display: "block",
                        color: isDark ? "#94a3b8" : "#64748b",
                      }}
                    >
                      QR Code: {generatedQR}
                    </Typography>
                  </Box>

                  {lastAddedItem && (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backdropFilter: "blur(10px)",
                        background: alpha(
                          isDark ? theme.palette.background.paper : "#f8fafc",
                          isDark ? 0.3 : 0.5
                        ),
                        border: `1px solid ${alpha(
                          isDark ? theme.palette.divider : "#e2e8f0",
                          isDark ? 0.2 : 0.3
                        )}`,
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: isDark ? "#e2e8f0" : "#475569",
                          mb: 1,
                        }}
                      >
                        {isGrossWeightProduct
                          ? "Gross Weight Product Added!"
                          : "Item Added Successfully!"}
                      </Typography>
                      <Stack spacing={0.5}>
                        {lastAddedItem.name && (
                          <Typography
                            variant="caption"
                            sx={{ color: isDark ? "#cbd5e1" : "#64748b" }}
                          >
                            {lastAddedItem.name} - {lastAddedItem.metal}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          sx={{ color: isDark ? "#cbd5e1" : "#64748b" }}
                        >
                          Net Weight: {lastAddedItem.weight}g
                        </Typography>
                        {lastAddedItem.price && (
                          <Typography
                            variant="caption"
                            sx={{ color: isDark ? "#cbd5e1" : "#64748b" }}
                          >
                            Final Price: ₹{calculateFinalPrice(lastAddedItem)}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={() => handlePrintLabel(lastAddedItem)}
                    disabled={
                      !lastAddedItem ||
                      isGrossWeightProduct ||
                      isUpdatingMasterData
                    }
                    sx={{
                      borderRadius: 3,
                      fontWeight: 600,
                      textTransform: "none",
                      width: "100%",
                      background: isDark
                        ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
                        : "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                      boxShadow: "0 4px 20px rgba(139, 92, 246, 0.25)",
                      py: 1.5,
                      "&:hover": {
                        boxShadow: "0 6px 24px rgba(139, 92, 246, 0.35)",
                        transform: "translateY(-2px)",
                      },
                      "&.Mui-disabled": {
                        background: alpha(isDark ? "#8b5cf6" : "#8b5cf6", 0.3),
                        boxShadow: "none",
                      },
                    }}
                  >
                    {isGrossWeightProduct
                      ? "Print Not Available"
                      : "Print Label"}
                  </Button>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Item Categories Management Dialog */}
      <Dialog
        open={itemDialogOpen}
        onClose={() => !isUpdatingMasterData && setItemDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backdropFilter: "blur(20px)",
            background: alpha(theme.palette.background.paper, 0.95),
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Manage Item Categories</Typography>
          <IconButton
            onClick={() => !isUpdatingMasterData && setItemDialogOpen(false)}
            disabled={isUpdatingMasterData}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {editingItem ? "Edit Item" : "Add New Item"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Item Name"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                disabled={isUpdatingMasterData}
                sx={{ flex: 2, minWidth: "200px" }}
              />
              <TextField
                size="small"
                placeholder="Shortcut (max 3)"
                value={newItemShortcut}
                onChange={(e) =>
                  setNewItemShortcut(e.target.value.toUpperCase().slice(0, 3))
                }
                disabled={isUpdatingMasterData}
                sx={{ flex: 1, minWidth: "100px" }}
                inputProps={{ maxLength: 3 }}
              />
              <Button
                variant="contained"
                onClick={
                  editingItem ? handleUpdateItemCategory : handleAddItemCategory
                }
                disabled={
                  !newItemName || !newItemShortcut || isUpdatingMasterData
                }
                sx={{ borderRadius: 2 }}
              >
                {editingItem ? "Update" : "Add"}
              </Button>
            </Box>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Existing Items
          </Typography>
          <List>
            {itemCategories.map((item) => (
              <ListItem
                key={item.id}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  mb: 1,
                  borderRadius: 2,
                }}
              >
                <ListItemText
                  primary={item.name}
                  secondary={`Shortcut: ${item.shortcut}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setEditingItem(item);
                      setNewItemName(item.name);
                      setNewItemShortcut(item.shortcut);
                      setItemDialogOpen(true);
                    }}
                    sx={{ mr: 1 }}
                    disabled={isUpdatingMasterData}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteItemCategory(item.id)}
                    color="error"
                    disabled={isUpdatingMasterData}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {/* Metal Types Management Dialog */}
      <Dialog
        open={metalDialogOpen}
        onClose={() => !isUpdatingMasterData && setMetalDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backdropFilter: "blur(20px)",
            background: alpha(theme.palette.background.paper, 0.95),
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Manage Metal Types</Typography>
          <IconButton
            onClick={() => !isUpdatingMasterData && setMetalDialogOpen(false)}
            disabled={isUpdatingMasterData}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {editingMetal ? "Edit Metal" : "Add New Metal"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Metal Name"
                value={newMetalName}
                onChange={(e) => setNewMetalName(e.target.value)}
                disabled={isUpdatingMasterData}
                sx={{ flex: 2, minWidth: "200px" }}
              />
              <TextField
                size="small"
                placeholder="Shortcut (max 2)"
                value={newMetalShortcut}
                onChange={(e) =>
                  setNewMetalShortcut(e.target.value.toUpperCase().slice(0, 2))
                }
                disabled={isUpdatingMasterData}
                sx={{ flex: 1, minWidth: "100px" }}
                inputProps={{ maxLength: 2 }}
              />
              <Button
                variant="contained"
                onClick={
                  editingMetal ? handleUpdateMetalType : handleAddMetalType
                }
                disabled={
                  !newMetalName || !newMetalShortcut || isUpdatingMasterData
                }
                sx={{ borderRadius: 2 }}
              >
                {editingMetal ? "Update" : "Add"}
              </Button>
            </Box>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Existing Metals
          </Typography>
          <List>
            {metalTypes.map((metal) => (
              <ListItem
                key={metal.id}
                sx={{
                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                  mb: 1,
                  borderRadius: 2,
                }}
              >
                <ListItemText
                  primary={metal.name}
                  secondary={`Shortcut: ${metal.shortcut}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setEditingMetal(metal);
                      setNewMetalName(metal.name);
                      setNewMetalShortcut(metal.shortcut);
                      setMetalDialogOpen(true);
                    }}
                    sx={{ mr: 1 }}
                    disabled={isUpdatingMasterData}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteMetalType(metal.id)}
                    color="error"
                    disabled={isUpdatingMasterData}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {/* Silver Types Management Dialog */}
      <Dialog
        open={silverTypeDialogOpen}
        onClose={() => !isUpdatingMasterData && setSilverTypeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backdropFilter: "blur(20px)",
            background: alpha(theme.palette.background.paper, 0.95),
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Manage Silver Types</Typography>
          <IconButton
            onClick={() =>
              !isUpdatingMasterData && setSilverTypeDialogOpen(false)
            }
            disabled={isUpdatingMasterData}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {editingSilverType ? "Edit Silver Type" : "Add New Silver Type"}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                size="small"
                label="Silver Type Code"
                placeholder="e.g., 999silver"
                value={newSilverType}
                onChange={(e) => setNewSilverType(e.target.value)}
                disabled={isUpdatingMasterData}
                fullWidth
                helperText="Internal code (lowercase, no spaces)"
              />
              <TextField
                size="small"
                label="Display Name"
                placeholder="e.g., 999 Silver"
                value={newSilverTypeDisplayName}
                onChange={(e) => setNewSilverTypeDisplayName(e.target.value)}
                disabled={isUpdatingMasterData}
                fullWidth
                helperText="Name shown in dropdowns"
              />
              <TextField
                size="small"
                label="Shortcut (max 3)"
                placeholder="e.g., 999"
                value={newSilverTypeShortcut}
                onChange={(e) =>
                  setNewSilverTypeShortcut(
                    e.target.value.toUpperCase().slice(0, 3)
                  )
                }
                disabled={isUpdatingMasterData}
                fullWidth
                inputProps={{ maxLength: 3 }}
              />
              <Button
                variant="contained"
                onClick={
                  editingSilverType
                    ? handleUpdateSilverType
                    : handleAddSilverType
                }
                disabled={!newSilverType || isUpdatingMasterData}
                sx={{ borderRadius: 2, mt: 1 }}
              >
                {editingSilverType ? "Update" : "Add"}
              </Button>
            </Box>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Existing Silver Types
          </Typography>
          <List>
            {silverTypes.map((type) => (
              <ListItem
                key={type.id}
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  mb: 1,
                  borderRadius: 2,
                }}
              >
                <ListItemText
                  primary={type.displayName || type.name}
                  secondary={`Code: ${type.name} | Shortcut: ${type.shortcut || type.name.substring(0, 3).toUpperCase()
                    }`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setEditingSilverType(type);
                      setNewSilverType(type.name);
                      setNewSilverTypeDisplayName(type.displayName || "");
                      setNewSilverTypeShortcut(type.shortcut || "");
                      setSilverTypeDialogOpen(true);
                    }}
                    sx={{ mr: 1 }}
                    disabled={isUpdatingMasterData}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteSilverType(type.id)}
                    color="error"
                    disabled={isUpdatingMasterData}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AddItemPage;
